import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExtraWorkOrder } from '../models';
import { useExtraWorkOrders } from './useExtraWorkOrders';
import { useQueryParams } from '@shared/hooks/useQueryParams';

type SortingState = { field: string; direction: 'asc' | 'desc' } | null;

const INITIAL_SELECTED_COLUMNS = [
  'ref',
  'status',
  'deadline_at',
  'merchandise',
  'location',
  'progress',
  'created_at',
  'actions',
] as const;

const FIXED_COLUMNS = ['ref', 'status'] as const;

const COLUMN_MAP: Record<string, string> = {
  ref: 'ref',
  status: 'status',
  team_id: 'team_id',
  entry_id: 'entry_id',
  user_id: 'user_id',
  location_id: 'location_id',
  started_at: 'started_at',
  finished_at: 'finished_at',
  deadline_at: 'deadline_at',
  created_at: 'created_at',
};

// 🎯 Mapeo de columnas de la tabla a columnas de la API para ordenamiento
// Columnas disponibles en la API: id, ref, order_created_at, processed_at, last_shipment_update
const FIELD_TO_API_SORT: Record<string, string> = {
  ref: 'ref',
  created_at: 'order_created_at', // Mapear created_at a order_created_at
  // Nota: id, processed_at, last_shipment_update no están disponibles en la tabla actual
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value as FilterConditionOutput[];
};

const computeAdvancedFilters = (
  conditions: FilterConditionOutput[],
  searchValue: string,
): { filters: any[]; logic: 'AND' | 'OR' } | null => {
  // 🎯 Mapear condiciones de filtro avanzado
  const mappedFilters = conditions
    .map((condition) => {
      const apiColumn = COLUMN_MAP[condition.column];
      if (!apiColumn) {
        return null;
      }

      const requiresArray = ['in', 'not_in'].includes(condition.action);
      const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

      const value = isNullOperator
        ? undefined
        : requiresArray
          ? Array.isArray(condition.value)
            ? condition.value
            : [condition.value]
          : condition.value;

      const filter: Record<string, unknown> = {
        column: apiColumn,
        operator: condition.action,
      };

      if (value !== undefined) {
        filter.value = value;
      }

      return filter;
    })
    .filter(Boolean) as any[];

  // 🎯 Agregar búsqueda por referencia (ref) con operador "contains" si hay searchValue
  // El campo de búsqueda simple siempre busca por referencia con "contains"
  if (searchValue && searchValue.trim()) {
    mappedFilters.push({
      column: 'ref',
      operator: 'contains',
      value: searchValue.trim(),
    });
  }

  // 🎯 Si no hay filtros, retornar null
  if (mappedFilters.length === 0) {
    return null;
  }

  // 🎯 Retornar filtros con lógica AND (todos los filtros deben cumplirse)
  return {
    filters: mappedFilters,
    logic: 'AND',
  };
};

export const useExtraWorkOrdersMain = () => {
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'ref' as string }, // Columna disponible en API
    sortDirection: { type: 'string', default: 'desc' as string },
  });

  const initialConditions = parseFiltersFromParams(queryParams.filters);

  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(initialConditions);
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(initialConditions));
  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortField = queryParams.sortBy || 'ref'; // Columna disponible en API
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';
    return {
      field: sortField,
      direction,
    };
  });
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...INITIAL_SELECTED_COLUMNS]);
  const [cachedOrders, setCachedOrders] = useState<ExtraWorkOrder[]>([]);
  const [cachedTotal, setCachedTotal] = useState(0);

  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy || 'ref'; // Columna disponible en API
    const direction = queryParams.sortDirection === 'asc' ? 'asc' : 'desc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const advancedFilters = useMemo(
    () => computeAdvancedFilters(filterConditions, searchTerm),
    [filterConditions, searchTerm],
  );

  // 🎯 Procesar ordenamiento: verificar que la columna tenga mapeo válido
  const processedSorting = useMemo(() => {
    if (!sorting?.field) {
      return null;
    }

    const apiField = FIELD_TO_API_SORT[sorting.field];
    // Si no hay mapeo, la columna no es ordenable en la API
    if (!apiField) {
      return null;
    }

    return {
      column: apiField,
      direction: sorting.direction ?? 'asc',
    };
  }, [sorting]);

  // 🎯 Construir filtros para la query usando advanced_filters (siguiendo convenciones del proyecto)
  // Cambio realizado: Migrar de parámetros individuales a advanced_filters
  // para mantener consistencia con el resto del proyecto y permitir filtros más flexibles
  // El campo de búsqueda simple siempre se incluye en advanced_filters como filtro "ref contains"
  const queryFilters = useMemo(() => {
    const baseFilters: any = {
      page_size: 15,
    };

    // 🎯 Agregar advanced_filters si hay filtros (incluye búsqueda por referencia si hay searchTerm)
    if (advancedFilters) {
      baseFilters.advanced_filters = advancedFilters;
    }

    // 🎯 Agregar ordenamiento si está configurado y tiene mapeo válido
    if (processedSorting) {
      baseFilters.sorting = {
        column: processedSorting.column,
        direction: processedSorting.direction,
      };
    }

    return baseFilters;
  }, [advancedFilters, processedSorting]);

  const {
    orders: fetchedOrders,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total: fetchedTotal,
  } = useExtraWorkOrders(queryFilters);

  useEffect(() => {
    if (fetchedOrders.length > 0) {
      setCachedOrders(fetchedOrders);
      setCachedTotal(fetchedTotal);
    }
  }, [fetchedOrders.length, fetchedTotal]);

  const handleSearch = useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      setSearchTerm(trimmedTerm);
      updateParam('search', trimmedTerm);
    },
    [updateParam],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);

      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: 'ref', // Columna disponible en API
          sortDirection: 'asc',
        });
      }
    },
    [updateParams],
  );

  // Detectar si hay filtros activos
  const hasActiveFilters = useMemo(
    () => filterConditions.length > 0 || Boolean(searchTerm.trim()),
    [filterConditions.length, searchTerm],
  );

  const shouldUseCachedData = !hasActiveFilters && fetchedOrders.length === 0 && cachedTotal > 0;

  const orders = shouldUseCachedData ? cachedOrders : fetchedOrders;
  const total = shouldUseCachedData ? cachedTotal : fetchedTotal;

  const ordersViewProps = useMemo(
    () => ({
      orders,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [orders, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  const exportColumns = useMemo(
    () => selectedColumns.map((columnId) => COLUMN_MAP[columnId]).filter(Boolean) as string[],
    [selectedColumns],
  );

  return {
    orders,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    searchTerm,
    ordersViewProps,
    exportColumns,
    hasActiveFilters,
    fixedColumns: [...FIXED_COLUMNS],
    filterConditions,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
  };
};
